/**
 * FILE: apps/admin/src/components/ActiveProjectStatusChip.tsx
 * PURPOSE: Status chips beside the project switcher (outside the trigger button)
 *          with rich tooltips for bottleneck, trend, and SDK signals.
 */

import { Link } from 'react-router-dom'
import type { ProjectSnapshot } from '../lib/projectSnapshotTypes'
import {
  PDCA_BOTTLENECK_TONE,
  bottleneckChipLabel,
  bottleneckDeepLink,
  bottleneckHumanHeadline,
  bottleneckHumanHint,
} from '../lib/pdcaBottleneck'
import { buildTrendTooltip } from '../lib/projectMetaTooltips'
import { resolveSdkDisplay } from '../lib/sdkVersionCompare'
import { MetricTooltipContent, Tooltip } from './ui'
import { CHIP_TONE } from '../lib/chipTone'
import { HEARTBEAT_HINT, HEARTBEAT_LABEL, heartbeatStateFromKeys } from '../lib/heartbeat'

interface ActiveProjectStatusChipProps {
  snapshot: ProjectSnapshot | null | undefined
  className?: string
}

export function ActiveProjectStatusChip({ snapshot, className = '' }: ActiveProjectStatusChipProps) {
  if (!snapshot) return null

  const bottleneck = snapshot.pdca_bottleneck
  const trend = snapshot.trend_7d
  const trendUp =
    trend && trend.direction === 'up' && (trend.last7d > 0 || trend.prev7d > 0)
  const sdkResolution = resolveSdkDisplay({
    observedVersion: snapshot.sdk_version ?? null,
    latestVersion: snapshot.sdk_latest_version ?? null,
    backendStatus: snapshot.sdk_status,
    deprecated: snapshot.sdk_status === 'deprecated',
  })
  const showSdkWarn =
    sdkResolution.kind === 'upgrade-available' || sdkResolution.kind === 'deprecated'

  // Connectivity is the precondition for every other signal on this rail —
  // an SDK-version warning is meaningless if no SDK is talking to us at all.
  // `unknown` means the payload carried no api_keys field, which is not
  // evidence of disconnection, so we stay silent in that case.
  const heartbeat = heartbeatStateFromKeys(snapshot.api_keys)
  const showHeartbeat = heartbeat !== 'unknown'

  if (
    !bottleneck &&
    !trendUp &&
    !showSdkWarn &&
    sdkResolution.kind !== 'catalog-ahead' &&
    !showHeartbeat
  ) {
    return null
  }

  const bottleneckCtx = bottleneck
    ? {
        stage: bottleneck,
        label: snapshot.pdca_bottleneck_label,
      }
    : null

  return (
    <span className={`inline-flex items-center gap-0.5 shrink-0 min-w-0 ${className}`.trim()}>
      {showHeartbeat && (
        <Tooltip
          content={
            <MetricTooltipContent
              data={{
                sections: [
                  {
                    kind: 'shows',
                    label: 'SDK heartbeat',
                    body: HEARTBEAT_LABEL[heartbeat],
                  },
                  {
                    kind: 'takeaway',
                    label: 'What it means',
                    body: HEARTBEAT_HINT[heartbeat],
                  },
                ],
                ...(heartbeat === 'fresh'
                  ? {}
                  : {
                      callout: {
                        tone: 'warn' as const,
                        text: 'Click to re-check the install snippet and keys.',
                      },
                    }),
              }}
            />
          }
          side="bottom"
          nowrap={false}
          portal
        >
          {heartbeat === 'fresh' ? (
            // Connected is the boring, expected case and this chip rides
            // beside the project switcher on every page — so the healthy
            // state is a bare dot, not another text pill competing for
            // attention. Only trouble gets words.
            <span
              className="inline-flex h-5 cursor-help items-center px-1"
              aria-label="SDK connected"
            >
              <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-ok" />
            </span>
          ) : (
            <Link
              to={`/connect?project=${encodeURIComponent(snapshot.id)}`}
              aria-label={`SDK ${HEARTBEAT_LABEL[heartbeat]} — open setup`}
              // mushi-mushi-allowlist: intentional arbitrary layout (calc/fr/%/canvas)
              className={`inline-flex h-5 max-w-[6.5rem] cursor-pointer items-center truncate rounded-sm px-1 text-2xs font-medium hover:opacity-90 ${
                heartbeat === 'never' ? CHIP_TONE.neutral : CHIP_TONE.warnSubtle
              }`}
            >
              {HEARTBEAT_LABEL[heartbeat]}
            </Link>
          )}
        </Tooltip>
      )}
      {bottleneck && snapshot.pdca_bottleneck_label && bottleneckCtx && (
        <Tooltip
          content={
            <MetricTooltipContent
              data={{
                sections: [
                  {
                    kind: 'shows',
                    label: 'Needs attention',
                    body: bottleneckHumanHeadline(bottleneckCtx),
                  },
                  {
                    kind: 'takeaway',
                    label: 'What to do',
                    body: bottleneckHumanHint(bottleneckCtx),
                  },
                ],
                callout: {
                  tone: 'info',
                  text: 'Click to open the page that clears this.',
                },
              }}
            />
          }
          side="bottom"
          nowrap={false}
          portal
        >
          <Link
            to={bottleneckDeepLink(
              bottleneck,
              snapshot.id,
              snapshot.pdca_bottleneck_label,
            )}
            // mushi-mushi-allowlist: intentional arbitrary layout (calc/fr/%/canvas)
            className={`inline-flex h-5 max-w-[6.5rem] cursor-pointer items-center truncate rounded-sm px-1 text-2xs font-semibold leading-none hover:opacity-90 ${PDCA_BOTTLENECK_TONE[bottleneck]}`}
          >
            {bottleneckChipLabel(bottleneckCtx)}
          </Link>
        </Tooltip>
      )}
      {trendUp && (
        <Tooltip
          content={<MetricTooltipContent data={buildTrendTooltip(snapshot)!} />}
          side="bottom"
          nowrap={false}
          portal
        >
          <span className={`inline-flex h-5 cursor-help items-center rounded-sm ${CHIP_TONE.warnSubtle} px-1 text-2xs font-mono font-semibold`}>
            ↑
          </span>
        </Tooltip>
      )}
      {(showSdkWarn || sdkResolution.kind === 'catalog-ahead') && (
        <Tooltip
          content={
            <MetricTooltipContent
              data={{
                sections: [
                  {
                    kind: 'shows',
                    label: 'SDK package',
                    body: `${snapshot.sdk_package ?? '@mushi-mushi/web'} v${snapshot.sdk_version ?? '?'}`,
                  },
                  {
                    kind: 'takeaway',
                    label: 'Status',
                    body:
                      sdkResolution.kind === 'upgrade-available'
                        ? `Upgrade available to v${sdkResolution.upgradeTarget}.`
                        : sdkResolution.kind === 'catalog-ahead'
                          ? 'Running a newer SDK than the publish catalogue lists — no action needed.'
                          : 'This SDK version is deprecated — plan a migration.',
                  },
                ],
              }}
            />
          }
          side="bottom"
          nowrap={false}
          portal
        >
          <Link
            to={`/connect?project=${encodeURIComponent(snapshot.id)}`}
            className={`inline-flex h-5 cursor-pointer items-center rounded-sm px-1 text-2xs font-medium hover:opacity-90 ${
              sdkResolution.kind === 'deprecated'
                ? CHIP_TONE.dangerSubtle
                : sdkResolution.kind === 'catalog-ahead'
                  ? CHIP_TONE.infoSubtle
                  : CHIP_TONE.warnSubtle
            }`}
          >
            SDK
          </Link>
        </Tooltip>
      )}
    </span>
  )
}
